import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AppContext } from '../contexts/AppContext';
import { Header as CoreHeader, Loading, Menu, SiderMenuLayout } from '@meteor/frontend-core';
import { BarChartOutlined, DashboardOutlined, DiffOutlined, FileSearchOutlined, LogoutOutlined, SettingOutlined } from '@ant-design/icons';
import TralitSettings from '../components/common/tralit-settings';
import { useLocation } from 'react-router-dom';
import TopPage from '../components/top/top-page';
import TicketPage from '../components/ticket/ticket-page';
import ChangePage from '../components/change/change-page';
import ToshibaProvider from '../contexts/provider/toshiba.provider';
import { ConfigProvider } from 'antd';
import { NAME_PATH, useTralitSetting } from '../hooks/useTralitSetting';
import DiffPage from '../components/diff/diff-page';
import { useSelectorSize } from '../hooks/useWindowSize';
import VersionManager from '../components/common/version-manager';

const ToshibaDashboard: React.FC = () => {
  const { color, menuCollapsed, selectedModule, authInfo, setMenuCollapsed, calender } = useContext(AppContext);
  const [showTralitSettings, setShowTralitSettings] = useState(false);
  const [userOperations, setUserOperations] = useState([]);
  const { fontSize } = useTralitSetting({ namePath: NAME_PATH.PREFERENCE });

  const location = useLocation();

  const curMenuItem = useMemo(() => {
    return location.pathname.split('/').at(-1);
  }, [location.pathname]);

  const { selectorSize: bodySize } = useSelectorSize({
    selector: 'body',
  });

  const sectionHeight = useMemo(() => bodySize.height - 50 - 37 - 16, [bodySize]);

  const router = useMemo(
    () => ({
      key: 'tralit',
      logoUrl: '/sbt.png',
      title: 'AI Powered Management Cockpit',
      label: 'AI Powered Management Cockpit',
      icon: <span>sbt</span>, // todo need a design
      description: 'tralit description.',
      // descI18n: 'menu.sbt.description',
      footerText: `Accenture ©${new Date().getFullYear()}`,
      menuExt: <VersionManager />,
      routes: [
        {
          key: 'achieve-summary',
          path: '/toshiba/achieve-summary',
          label: '業績サマリー',
          icon: <DashboardOutlined />,
          description: '業績サマリー',
        },
        {
          key: 'achieve-detail',
          path: '/toshiba/achieve-detail?evaluationPeriod=全期',
          label: '業績詳細',
          icon: <FileSearchOutlined />,
          description: '業績詳細',
        },
        {
          key: 'impact-analysis',
          path: '/toshiba/impact-analysis?evaluationPeriod=全期',
          label: '受注変動分析',
          icon: <BarChartOutlined />,
          description: '受注変動分析',
        },
        {
          key: 'achieve-diff-analysis',
          path: '/toshiba/achieve-diff-analysis?evaluationPeriod=全期',
          label: '業績差分分析',
          icon: <DiffOutlined />,
          description: '業績差分分析',
        },
      ],
    }),
    []
  );

  useEffect(() => {
    const operations = [];
    if (process.env.REACT_APP_DISABLE_AUTH !== 'true') {
      operations.push({
        key: 'logout',
        label: (
          <span>
            <LogoutOutlined />
            <span style={{ padding: 8 }}>Logout</span>
          </span>
        ),
        onSelect: async () => {
          await authInfo.signOut();
        },
      });
    }
    setUserOperations(operations);
  }, []);

  const ToshibaComponent = useCallback(() => {
    if (!calender) {
      return (
        <>
          <Loading />
        </>
      );
    }
    if (location.pathname.includes('/toshiba/achieve-summary')) {
      return <TopPage />;
    }
    if (location.pathname.includes('/toshiba/achieve-detail')) {
      return <TicketPage />;
    }
    if (location.pathname.includes('/toshiba/impact-analysis')) {
      return <ChangePage />;
    }
    if (location.pathname.includes('/toshiba/achieve-diff-analysis')) {
      return <DiffPage />;
    }
    return <></>;
  }, [location.pathname, calender]);
  return (
    <>
      <ToshibaProvider>
        <ConfigProvider
          theme={{
            components: {
              Menu: {
                itemSelectedColor: '#E90E21',
                itemMarginBlock: 5,
                itemHeight: 30,
                fontSize: fontSize.menu,
              },
              Table: {
                cellFontSize: fontSize.table,
                cellFontSizeMD: fontSize.table,
                cellFontSizeSM: fontSize.table,
              },
            },
          }}
        >
          <SiderMenuLayout
            header={
              <CoreHeader
                moduleInfo={selectedModule}
                userInfo={authInfo}
                color={color}
                otherOperations={[
                  <>
                    <div
                      style={{ float: 'right', paddingLeft: 25 }}
                      onClick={() => {
                        setShowTralitSettings(!showTralitSettings);
                      }}
                    >
                      <SettingOutlined
                        style={{
                          fontSize: 24,
                          marginRight: 8,
                          top: 4,
                          position: 'relative',
                          color: color.theme === 'dark' ? '#ffffff' : color.primaryColor,
                        }}
                      />
                      <span
                        style={{
                          fontSize: 14,
                          fontWeight: 'bolder',
                          top: 1,
                          position: 'relative',
                          paddingRight: 12,
                          color: color.theme === 'dark' ? '#ffffff' : color.primaryColor,
                        }}
                      >
                        設定
                      </span>
                    </div>
                  </>,
                  <TralitSettings showSettings={showTralitSettings} setShowSettings={setShowTralitSettings} />,
                ]}
                userOperations={userOperations}
              />
            }
            content={
              <section style={{ height: sectionHeight }}>
                <ToshibaComponent />
              </section>
            }
            contentLoading={false}
            color={color}
            collapsed={menuCollapsed}
            onCollapsed={setMenuCollapsed}
            moduleConfig={router}
            menu={<Menu selectedMenuKey={curMenuItem} menuItems={router.routes} color={color} menuCollapsed={menuCollapsed} />}
          />
        </ConfigProvider>
      </ToshibaProvider>
    </>
  );
};
export default ToshibaDashboard;
